/**
 * client/src/pages/security-centre/master/use-master-ceremony.ts
 *
 * Device-limit + master-passkey spec PR B (#1605, module 02 §3/§5). Generic
 * begin -> assert -> complete runner for the five master ceremonies
 * (designate, arming-cancel, instant transfer, hold initiate, veto) — same
 * proven shape as useStepUpAAL3 (client/src/lib/use-step-up-aal3.ts) and
 * useStampCeremony, parameterised over begin/complete so this one hook backs
 * all five call sites instead of five near-identical copies.
 *
 * Cross-OS honesty (module 02 §5): when the account's master lives on a
 * different OS than the one initiating the ceremony, `allowCredentials`
 * (server-built, carrying the master's `transports`) makes the BROWSER offer
 * its native hybrid/QR flow automatically inside `startAuthentication` — no
 * client code needed to "surface" it. This hook's only honesty job is the
 * copy shown WHILE that native prompt might appear: `LOADING_COPY` below,
 * never a stronger claim than "confirm with your master passkey" (never a
 * live-human or deepfake claim, ledger #16 / spec module 03 §5).
 *
 * No client signing keys — every ceremony only relays the assertion (Rule 11).
 */

import { useState, useCallback } from "react";
import type { PublicKeyCredentialRequestOptionsJSON, AuthenticationResponseJSON } from "@simplewebauthn/browser";
import { ApiError } from "@/lib/queryClient";
import { decodePrfExtensionInputs } from "@/lib/webauthn-prf-extension";
import { startLocalFirstAuthentication } from "@/lib/webauthn/local-credential";
import { isUserCancel } from "@/lib/payment-rail/webauthn-cancel";
import { resolveWebAuthnError } from "@/lib/webauthn-errors";
import { NETWORK_PROBLEM_MESSAGE } from "@/lib/error-copy";

/** Honest, cross-OS-aware loading copy — the ceremony may resolve via a
 *  cross-device hybrid/QR prompt when the master lives on another device
 *  (module 02 §5). Never claims live-human presence. */
export const MASTER_CEREMONY_LOADING_COPY =
  "Confirm with your master passkey. If it's registered on another device, a scan-to-confirm prompt will appear.";

export interface MasterCeremonyResult<T> {
  run: () => Promise<T | undefined>;
  isRunning: boolean;
  /** Amber-tier, user-readable copy (never red — CLAUDE.md colour contract). */
  error: string | null;
  /** Raw server error code behind `error` (e.g. MASTER_HARDWARE_CONFLICT),
   *  or null for a user-cancel/network failure/idle. Consumers branch on
   *  this to render a specific dead-end rather than string-matching. */
  errorCode: string | null;
  reset: () => void;
}

/**
 * @param beginFn    Issues the fresh-UV challenge (a master route's `begin`).
 * @param completeFn Relays the assertion to the matching `complete` route.
 */
export function useMasterCeremony<T>(
  beginFn: () => Promise<{ options: PublicKeyCredentialRequestOptionsJSON }>,
  completeFn: (response: AuthenticationResponseJSON) => Promise<T>,
): MasterCeremonyResult<T> {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const reset = useCallback(() => {
    setError(null);
    setErrorCode(null);
  }, []);

  const run = useCallback(async (): Promise<T | undefined> => {
    setIsRunning(true);
    setError(null);
    setErrorCode(null);

    try {
      let begin: { options: PublicKeyCredentialRequestOptionsJSON };
      try {
        begin = await beginFn();
      } catch (err) {
        applyError(err, setError, setErrorCode);
        return undefined;
      }

      const decodedOptions = decodePrfExtensionInputs(begin.options);

      let assertionResponse: AuthenticationResponseJSON;
      try {
        // Local-credential-first (ledger #88, #2876). The cross-OS honesty above
        // is unchanged: the seam only narrows when THIS device is the one that
        // answered for the offered credential, so a master living on another
        // device finds no match and keeps its hybrid/QR transports verbatim.
        assertionResponse = await startLocalFirstAuthentication(decodedOptions);
      } catch (err) {
        if (isUserCancel(err)) {
          setError("You cancelled the passkey prompt. Tap the button again to retry.");
          setErrorCode(null);
          return undefined;
        }
        applyError(err, setError, setErrorCode);
        return undefined;
      }

      try {
        return await completeFn(assertionResponse);
      } catch (err) {
        applyError(err, setError, setErrorCode);
        return undefined;
      }
    } finally {
      setIsRunning(false);
    }
  }, [beginFn, completeFn]);

  return { run, isRunning, error, errorCode, reset };
}

function applyError(
  err: unknown,
  setError: (v: string) => void,
  setErrorCode: (v: string | null) => void,
): void {
  if (err instanceof ApiError) {
    setError(resolveWebAuthnError(err).description);
    setErrorCode(err.code ?? null);
    return;
  }
  if (err instanceof TypeError) {
    setError(NETWORK_PROBLEM_MESSAGE);
    setErrorCode(null);
    return;
  }
  setError(resolveWebAuthnError(err).description);
  setErrorCode(null);
}
